import React, { useState, useEffect } from 'react';

import _ from 'lodash';
import cx from 'classnames';

import { Fonts } from '@paperflite-marketing/cleverstory-commons';
import { FORM_CHECK_BOX, FORM_STEP } from '../../../constants/widgetTypes';

import { FormContext, WidgetsContext } from '../core/context';
import { useFormInputs, useOtpFormInput } from '../core/hooks';
import { getColorsConfig } from '../../../components/AssetViewer/helper/cssHelper';
import { injectAktivFallback } from '../widget.helper';

import styles from './form.module.scss';

export const FormWidgetContext = React.createContext({
  updateField: () => { },
  fields: {},
  onFormSubmit: () => { },
  onOtpFormSubmit: () => { },
  onOtpResend: () => { }
});

const FormWidget = function FormEditor({ className, style, widgetProps, breakpoint, children }) {
  const context = React.useContext(WidgetsContext);
  const formContext = React.useContext(FormContext);

  const [state, setState] = useState({
    formStyles: {},
    step: ''
  });

  const [formContextState, setFormContextState] = useState({
    errors: {},
    fields: []
  })
  const id = _.get(widgetProps, 'uid');
  const formSuccess = _.get(widgetProps, 'success', false);
  const otpSuccess = _.get(widgetProps, 'otpSuccess', false);
  const submitting = _.get(widgetProps, 'submitting', false);
  const formErrors = _.get(widgetProps, 'error');
  const formOtpId = _.get(widgetProps, 'formOtpId');
  const isOtpRequired = _.get(widgetProps, `properties.${breakpoint}.emailOtp`, false);

  var formSteps = children.filter(item => _.get(item, 'props.widgetProps.type') === FORM_STEP)
    .map(item => _.get(item, 'props.widgetProps'));

  const inputWidgets = useFormInputs(id);
  const otpInputWidget = useOtpFormInput(id);

  const properties = _.get(widgetProps, 'properties', {});
  const deviceProperties = _.get(properties, `${breakpoint}`, {});

  Fonts.useFont(_.get(widgetProps, 'fonts') || []);

  useEffect(() => {
    formSteps = _.orderBy(formSteps, ['order'], ['asc']);
    const formStep = formSteps[0];

    setState(state => {
      return {
        ...state,
        step: _.get(widgetProps, 'step', _.get(formStep, 'uid', null))
      }
    })
  }, [_.get(widgetProps, 'step')]);

  useEffect(() => {
    setFormContextState(state => {
      return {
        ...state,
        updateField: handleUpdateField,
        onFormSubmit: handleFormSubmit,
        onOtpFormSubmit: handleOtpFormSubmit,
        onOtpResend: handleOtpResend,
        submitting: false
      }
    });
  }, []);

  useEffect(() => {
    if (formSuccess) {
      formSteps = _.orderBy(formSteps, ['order'], ['desc']);
      let formStep;

      if(formSteps?.length > 2){
        formStep = isOtpRequired ? formSteps[0] : formSteps[1];
      } else {
        formStep = formSteps[0];
      }
      
      setState(state => {
        return {
          ...state,
          step: _.get(widgetProps, 'step', _.get(formStep, 'uid', null))
        }
      })
    }
  }, [formSuccess,isOtpRequired]);

  useEffect(() => {
    if (otpSuccess) {
      formSteps = _.orderBy(formSteps, ['order'], ['desc']);
      const formStep = formSteps[1];

      setState(state => {
        return {
          ...state,
          step: _.get(widgetProps, 'step', _.get(formStep, 'uid', null))
        }
      })
    }
  }, [otpSuccess]);

  useEffect(() => {
    setFormContextState(state => {
      return {
        ...state,
        submitting: submitting
      }
    });
  }, [submitting]);

  useEffect(() => {
    setFormContextState((state) => {
      if (formErrors && formErrors.email) {
        const emailField = inputWidgets.find(widget => _.get(widget, 'properties.desktop.field') === 'email');
        if (emailField) {
          const { email, ...restErrors } = formErrors;
          return {
            ...state,
            errors: {
              ...restErrors,
              [emailField.uid]: email
            }
          };
        }
      }

      return {
        ...state,
        errors: formErrors || {}
      }
    });
  }, [formErrors]);

  useEffect(() => {
    var formStyles = getStyles(properties, breakpoint);
    setState(state => {
      return {
        ...state,
        formStyles: formStyles
      }
    })
  }, [deviceProperties]);

  useEffect(() => {
    setFormContextState(state => {
      return {
        ...state,
        updateField: handleUpdateField,
        onFormSubmit: handleFormSubmit,
        onOtpFormSubmit: handleOtpFormSubmit,
        onOtpResend: handleOtpResend,
      }
    });
  }, [formContextState.fields]);

  var widgetStyle = {
    ...style,
    ...state.formStyles
  }

  const handleUpdateField = (uid, field, fieldId, value) => {
    setFormContextState(state => {
      var fields = state.fields || [];
      fields = fields.filter(item => item.uid != uid);
      fields = [...fields, {
        uid: uid,
        id: field,
        fieldId: fieldId,
        value: value
      }];

      let errors = _.omit(state.errors, uid);

      return {
        ...state,
        fields: fields,
        errors: errors
      }
    })
  }

  const handleFormSubmit = () => {
    let errors = validateForm();

    if (Object.keys(errors).length > 0) {
      setFormContextState(state => {
        return {
          ...state,
          errors: errors
        }
      });

      return;
    }

    formContext.onFormSubmit(context.session, id, {
      fields: formContextState.fields,
      parent: context.parent,
      eventUid: context.eventUid,
      gatingTrigger: context.gatingTrigger,
      smart: context.smart
    });
  };

  const handleOtpFormSubmit = () => {
    let errors = validateOtpForm();

    if (Object.keys(errors).length > 0) {
      setFormContextState(state => {
        return {
          ...state,
          errors: errors
        }
      });

      return;
    }

    const otpObject = formContextState?.fields?.find(item => item.id === "otp");
    const otp = otpObject?.value

    formContext.onOtpFormSubmit(context.session, id, {
      fields: formContextState.fields,
      parent: context.parent,
      formOtpId: formOtpId && formOtpId,
      otp:otp && otp,
      eventUid: context.eventUid,
      gatingTrigger: context.gatingTrigger,
      smart: context.smart
    });
  };

  const handleOtpResend = () => {

    formContext.onOtpResend(context.session, id, {
      formOtpId:formOtpId
    });
  };

  const validateForm = () => {
    var errors = {};
    (inputWidgets || []).forEach(widget => {
      var field = (formContextState.fields || []).find(field => {
        return _.get(widget, 'uid', '') === field.uid;
      })

      var value = _.get(field, 'value', '');
      var uid = _.get(widget, 'uid', null);
      var properties = _.get(widget, 'properties.desktop', {});
      var mandatory = properties.mandatory;

      if (properties.field === 'email' && !validateEmail(value)) {
        errors[uid] = `Please provide a valid ${properties.label || 'value'}`;
        return
      }

      if (mandatory && (!value || (Array.isArray(value) && value.length === 0) || value === false)) {
        if(_.get(widget, 'type') == FORM_CHECK_BOX){
          errors[uid] = `This field cannot be empty`;
          return
        }
        errors[uid] = `Please provide a valid ${properties.label || 'value'}`;
      }
    })

    return errors;
  }

  const validateOtpForm = () => {
    var errors = {};
    (otpInputWidget || []).forEach(widget => {
      var field = (formContextState.fields || []).find(field => {
        return _.get(widget, 'uid', '') === field.uid;
      })

      var value = _.get(field, 'value', '');
      var uid = _.get(widget, 'uid', null);
      var properties = _.get(widget, 'properties.desktop', {});
      var mandatory = properties.mandatory;

      if (mandatory && properties.field === 'otp' && !validateOtp(value)) {
        errors[uid] = `Please provide a valid otp`;
        return
      }
    })

    return errors;
  }

  const validateEmail = (email) => {
    if (!email) {
      return false;
    }

    const regex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return regex.test(email);
  }

  const validateOtp = (otp, requiredLength = 4) => {
    if (!otp) {
      return false;
    }
  
    const trimmedOtp = otp.trim();
  
    if (trimmedOtp.length !== requiredLength) {
      return false;
    }
  
    return trimmedOtp;
  };

  return (
    <FormWidgetContext.Provider value={formContextState}>
      <div id={id}
        className={cx(className, styles.form)}
        style={widgetStyle}
        data-widget-id={id}>
        {
          children.filter(item => _.get(item, 'props.widgetProps.uid') === state.step)
        }
      </div>
    </FormWidgetContext.Provider>
  )
};

/**
 * Get Styles for the properties
*/
function getStyles(properties, breakpoint) {
  var styles = {};

  if (!properties) {
    return styles;
  }

  const defaultProperties = _.get(properties, `desktop`);
  const deviceProperties = _.get(properties, `${breakpoint}`);

  const font = _.get(defaultProperties, 'font', {});

  //font-color
  const colorRgb = _.get(font, 'color', null);
  const color = getColorStyles(colorRgb);
  styles = {
    ...styles,
    ...color
  }

  //font-family
  const fontFamily = _.get(font, 'fontFamily', 'inherit');
  styles = {
    ...styles,
    fontFamily: injectAktivFallback(fontFamily)
  }

  //font-size
  const fontSizeString = _.get(deviceProperties, 'regular.font.fontSize', null);
  var fontSize = 'inherit'
  if (fontSizeString) {
    fontSize = parseInt(fontSizeString) + 'px';
  }

  styles['fontSize'] = fontSize;


  //padding
  const paddingString = _.get(deviceProperties, 'padding', null)
  var padding = '20'
  if (paddingString) {
    padding = parseInt(paddingString) + 'px';
  }

  styles['padding'] = padding;


  //font-style
  const bold = _.get(font, 'style.bold', false);
  if (bold) {
    styles['fontWeight'] = 'bold';
  }

  const italic = _.get(font, 'style.italic', false);
  if (italic) {
    styles['fontStyle'] = 'italic';
  }

  const underline = _.get(font, 'style.underline', false);
  if (underline) {
    styles['textDecoration'] = 'underline';
  }

  //background
  const backgroundRgb = _.get(defaultProperties, 'background', null);
  var background = 'inherit';
  if (backgroundRgb) {
    background = getColorsConfig(backgroundRgb);
  }

  styles['background'] = background;

  //border
  const border = _.get(defaultProperties, 'border', {});
  const borderStyle = getBorderStyles(border);
  styles = {
    ...styles,
    ...borderStyle
  }

  //alignment
  const alignment = _.get(deviceProperties, 'alignment', {});

  var justifyContent = _.get(alignment, 'justifyContent', 'left');
  styles['justifyContent'] = justifyContent;

  var flexDirection = _.get(alignment, 'flexDrection', 'column');
  styles['flexDirection'] = flexDirection;

  var alignItems = _.get(alignment, 'alignItems', 'start');
  styles['alignItems'] = alignItems;

  var overflow = "visible"
  styles['overflow'] = overflow;

  return styles;
}

export function getColorStyles(value) {
  if (!value) {
    return null;
  }

  const colorRgb = value;

  var color = 'inherit';
  if (colorRgb) {
    color = getColorsConfig(colorRgb);
  }

  var styles = {
    color: color
  };

  return styles;
}

export function getBorderStyles(value) {
  if (!value) {
    return null;
  }

  var borderStyles = {}

  const topLeft = _.get(value, 'radius.topLeft', null);
  const topRight = _.get(value, 'radius.topRight', null);
  const bottomLeft = _.get(value, 'radius.bottomLeft', null);
  const bottomRight = _.get(value, 'radius.bottomRight', null);

  if (topLeft) {
    borderStyles['borderTopLeftRadius'] = parseInt(topLeft) + 'px';
  }

  if (topRight) {
    borderStyles['borderTopRightRadius'] = parseInt(topRight) + 'px';
  }

  if (bottomLeft) {
    borderStyles['borderBottomLeftRadius'] = parseInt(bottomLeft) + 'px';
  }

  if (bottomRight) {
    borderStyles['borderBottomRightRadius'] = parseInt(bottomRight) + 'px';
  }

  const colorRgb = _.get(value, 'color');

  var color = 'transparent';
  if (colorRgb) {
    color = getColorsConfig(colorRgb);
  }

  borderStyles['borderColor'] = color;

  const borderWidthString = _.get(value, 'width', 0);
  var borderWidget = parseInt(borderWidthString) + 'px';

  borderStyles['borderWidth'] = borderWidget;

  return borderStyles;
}

export { FormWidget };
